import { useState } from "react";
import { Hideable } from "shared/UI/Hideable/Hideable";
import useComponentVisible from "shared/UI/Hooks/useComponentVisible";
import { Input } from "shared/UI/Input/Input";
import "./Typeahead.css";

export type Suggestion<TValue> = {
  value: TValue | undefined;
  displayText: string | undefined;
};

type TypeaheadProps<TValue> = {
  placeholder: string;
  suggestions: Suggestion<TValue>[];
  onTextChanged: (value: string) => void;
  onItemSelected: (item?: Suggestion<TValue>) => void;
  readOnly?: boolean;
  initialText?: string;
  autoFill?: boolean;
  label?: string;
};

export const Typeahead = <TValue extends any>({
  placeholder,
  suggestions,
  onTextChanged,
  onItemSelected,
  initialText,
  readOnly,
  autoFill = true,
  ...rest
}: TypeaheadProps<TValue>) => {
  const [inputValue, setInputValue] = useState(initialText ?? "");
  const { targetAreaRef, isComponentVisible, setIsComponentVisible } = useComponentVisible<HTMLDivElement>(false);

  function onTextChangedWrapper(value: string) {
    setInputValue(value);
    showHideSuggestions(value);
    onTextChanged(value);

    if (value.trim().length === 0) {
      onItemSelected(undefined);
    }
  }

  function onItemSelectedWrapper(selectedValue: Suggestion<TValue>) {
    if (autoFill) {
      setInputValue(selectedValue.displayText ?? "");
    }
    onItemSelected(selectedValue);
    setIsComponentVisible(false);
  }

  function showHideSuggestions(value: string) {
    setIsComponentVisible(value.trim().length > 0 && readOnly !== true);
  }

  return (
    <div ref={targetAreaRef} className="relative w-full">
      <Input
        className="relative z-30"
        placeholder={placeholder}
        onFocus={(e) => showHideSuggestions(e.target.value)}
        onChange={(e) => onTextChangedWrapper(e.target.value)}
        value={inputValue}
        readOnly={readOnly}
        {...rest}
      />
      <Hideable hidden={!isComponentVisible}>
        <div className="typeahead-scrollbar rounded-b absolute z-20 top-full -translate-y-2 bg-l-blue w-full max-h-[50vh] overflow-y-auto shadow-md">
          {suggestions.map((s, i) => (
            <div key={i} className="first:pt-5 cursor-pointer border-b last:border-none border-navy-blue-lighter block w-full py-3 px-4 text-left hover:bg-navy-blue-lighter active:bg-navy-blue-lighter focus:bg-navy-blue-lighter">
              <button className="text-navy-blue font-bold w-full text-left" aria-label={`suggestion: ${s.displayText}`} onClick={() => onItemSelectedWrapper(s)}>
                {s.displayText}
              </button>
            </div>
          ))}
        </div>
      </Hideable>
    </div>
  );
};
